import styled from "@emotion/styled/macro";
import {
  MOBILE_BREAKPOINT,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { ReactNode } from "react";

type TypographyWeight = "regular" | "medium";

type PaymentLineItemProps = {
  label: ReactNode;
  value: ReactNode;
  unit?: string;
  labelSize?: number;
  labelWeight?: TypographyWeight;
  valueSize?: number;
  valueWeight?: number;
  valueLineHeight?: number;
  labelAccessory?: ReactNode;
  labelFootnote?: ReactNode;
  className?: string;
  maxLabelWidth?: number;
};

const PaymentLineItem = ({
  label,
  value,
  unit,
  labelSize = 14,
  labelWeight = "regular",
  valueSize = 16,
  valueWeight = 400,
  valueLineHeight = 1.2,
  labelAccessory,
  labelFootnote,
  className,
  maxLabelWidth,
}: PaymentLineItemProps) => (
  <LineItem className={className}>
    <LineItemLabel>
      <LabelWithAccessory
        $maxWidth={maxLabelWidth}
        data-testid="payment-line-item-label"
      >
        {renderPaymentLineItemLabel({
          label,
          labelAccessory,
          labelSize,
          labelWeight,
        })}
      </LabelWithAccessory>
      {labelFootnote}
    </LineItemLabel>
    <PaymentValue
      $size={valueSize}
      $weight={valueWeight}
      $lineHeight={valueLineHeight}
    >
      {value}
      {unit && <PaymentUnit>{unit}</PaymentUnit>}
    </PaymentValue>
  </LineItem>
);

const formatValidUntilDate = (expires: string) =>
  new Date(expires).toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });

const PaymentValidUntil = ({
  expires,
  className,
}: {
  expires: string;
  className?: string;
}) => (
  <ValidUntilText
    size={12}
    color={tokens.BRAND_MEDIUM_BLUE}
    className={className}
  >
    Valid until {formatValidUntilDate(expires)}
  </ValidUntilText>
);

const PaymentCard = styled.div<{ $opacity: number }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 10px;
  background: color-mix(
    in srgb,
    ${tokens.WHITE} ${({ $opacity }) => $opacity * 100}%,
    transparent
  );
  backdrop-filter: blur(8px);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 20px 16px;
  }
`;

const PaymentCardHeader = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? "column" : "row")};
  align-items: ${({ $isMobile }) => ($isMobile ? "flex-start" : "center")};
  justify-content: space-between;
  gap: 12px;
`;

const PaymentColumns = styled.div<{ $isMobile: boolean; $columnGap?: number }>`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${({ $isMobile, $columnGap = 24 }) =>
    !$isMobile &&
    `
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: ${$columnGap}px;
    row-gap: 24px;
    align-items: start;
  `}
`;

const PaymentColumn = styled.div<{ gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap = 24 }) => gap}px;
`;

const PaymentGroup = styled.div<{ $tight?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $tight }) => ($tight ? "2px" : "8px")};
`;

const PaymentGroupTitle = styled(Typography)`
  line-height: 1.4 !important;
  letter-spacing: -0.02em;
  font-weight: 600 !important;
`;

const PaymentRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LineItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const LineItemLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

const LabelWithAccessory = styled.div<{ $maxWidth?: number }>`
  display: block;
  ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth}px;`}
`;

const LabelAccessoryInline = styled.span`
  display: inline-flex;
  vertical-align: middle;
  margin-left: 6px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const LabelLastWordCluster = styled.span`
  display: inline;
  white-space: nowrap;
`;

type RenderPaymentLineItemLabelArgs = {
  label: ReactNode;
  labelAccessory: ReactNode | undefined;
  labelSize: number;
  labelWeight: TypographyWeight;
};

const renderPaymentLineItemLabel = ({
  label,
  labelAccessory,
  labelSize,
  labelWeight,
}: RenderPaymentLineItemLabelArgs) => {
  const labelTypography = (children: ReactNode) => (
    <Typography
      component="span"
      variant="span"
      size={labelSize}
      fontWeight={labelWeight}
      color={tokens.BRAND_HERO_BLUE}
    >
      {children}
    </Typography>
  );

  if (!labelAccessory) {
    return labelTypography(label);
  }

  if (typeof label === "string") {
    const trimmed = label.trim();
    const lastSpace = trimmed.lastIndexOf(" ");

    if (lastSpace === -1) {
      return labelTypography(
        <LabelLastWordCluster data-testid="label-last-word-cluster">
          {trimmed}
          <LabelAccessoryInline>{labelAccessory}</LabelAccessoryInline>
        </LabelLastWordCluster>
      );
    }

    const leading = trimmed.slice(0, lastSpace);
    const lastWord = trimmed.slice(lastSpace + 1);

    return labelTypography(
      <>
        {leading}{" "}
        <LabelLastWordCluster data-testid="label-last-word-cluster">
          {lastWord}
          <LabelAccessoryInline>{labelAccessory}</LabelAccessoryInline>
        </LabelLastWordCluster>
      </>
    );
  }

  return (
    <>
      {labelTypography(label)}
      <LabelAccessoryInline>{labelAccessory}</LabelAccessoryInline>
    </>
  );
};

const PaymentValue = styled.div<{
  $size: number;
  $weight: number;
  $lineHeight: number;
}>`
  font-size: ${({ $size }) => $size}px;
  font-weight: ${({ $weight }) => $weight};
  line-height: ${({ $lineHeight }) => $lineHeight};
  letter-spacing: -0.02em;
  color: ${tokens.BRAND_HERO_BLUE};
  text-align: right;
  flex-shrink: 0;
`;

const PaymentUnit = styled.span`
  font-size: 14px;
  font-weight: 400;
`;

const PaymentFinePrint = styled.p`
  margin: 0;
  max-width: 392px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: ${tokens.TINTS_OFF_WHITE_20};
`;

const ValidUntilText = styled(Typography)`
  line-height: 140% !important;
`;

const PaymentGroupTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export {
  PaymentCard,
  PaymentCardHeader,
  PaymentColumn,
  PaymentColumns,
  PaymentFinePrint,
  PaymentGroup,
  PaymentGroupTitle,
  PaymentLineItem,
  PaymentRows,
  PaymentValidUntil,
  PaymentValue,
  PaymentUnit,
  PaymentGroupTitleRow,
};
